import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useTranslation } from 'next-i18next'

import { TransportationLine } from '~/domains/spaces/components/atoms/transportation-line'
import { useSpaceStations } from '~/domains/spaces/hooks/use-space-stations.hook'
import { cn } from '~/lib/utils'

const TransportationLineItems = ({
	space,
	station,
	showStationName,
	className,
}: {
	space: Pick<Space, 'name' | 'zone' | 'nearest_stations'>
	station: NonNullable<ReturnType<typeof useSpaceStations>['stations']>[number]
	showStationName: boolean
	className?: string
}) => {
	return (
		<div className={cn('flex flex-col gap-y-1', className)}>
			{station?.lines?.map((line, index) => (
				<div
					key={index}
					className='flex flex-row gap-x-2'
				>
					<TransportationLine
						space={space}
						stationName={station?.name || ''}
						line={line}
					/>

					{showStationName && <div className='text-sm'>{station?.name}</div>}
				</div>
			))}
		</div>
	)
}

export const TransportationAccessItems = ({
	space,
	showStationName = true,
	className,
}: {
	space: Pick<Space, 'name' | 'zone' | 'nearest_stations'>
	showStationName?: boolean
	className?: string
}) => {
	const { t } = useTranslation()
	const { stations } = useSpaceStations(space)

	return (
		<div className={className}>
			{stations?.map((station, index) => (
				<div key={index}>
					<div className='mb-1 text-sm'>
						{station.name}
						&nbsp;
						<span className='text-xs'>
							{t('space:sections.location.station_duration', {
								duration: station.duration,
							})}
						</span>
					</div>

					<TransportationLineItems
						space={space}
						station={station}
						showStationName={showStationName}
					/>
				</div>
			))}
		</div>
	)
}

export const TransportationAccessTitle = ({
	className,
}: {
	className?: string
}) => {
	const { t } = useTranslation()

	return (
		<div className={cn('mb-4 font-semibold', className)}>
			{t('space:sections.location.nearest_stations')}
		</div>
	)
}

export const TransportationAccess = ({
	space,
	className,
}: {
	space: Pick<Space, 'name' | 'zone' | 'nearest_stations'>
	className?: string
	itemsClassName?: string
	titleClassName?: string
}) => {
	return (
		<div className={className}>
			<TransportationAccessTitle />

			<TransportationAccessItems
				space={space}
				showStationName={false}
				className='flex flex-col gap-1'
			/>
		</div>
	)
}
